import EmailTemplate from "../../../model2/email-template/email-template"
import {EmailContentType} from "../../../model2/email-template/const"

// Ticket emails are a fixed set - these ids are both the settings keys and the
// EmailTemplate ids, so a row in the settings list maps straight back to its slot
export enum TicketEmailTemplateId {
    NEW_TICKET      = 'newTicket',
    REPLY           = 'reply',
    CLOSE_TICKET    = 'closeTicket'
}

// Body/subject tokens are the ticket mailer's: {TICKET-ID}, {TICKET-TITLE}, {TICKET-MESSAGE},
// {TICKET-URL}, {CUSTOMER-NAME}, {COMPANY-NAME}. The {TASK-*} tokens are project automation's
// and are not substituted here - see docs/code/support-tickets-frontend.md.

// Same subject on all 3 so the customer's mail client threads them as one conversation
const SUBJECT = '[{TICKET-ID}] {TICKET-TITLE}'

export const DEFAULT_FROM_NAME = '{COMPANY-NAME} Support'

const REPLY_HINT = 'You can also simply reply to this email and your answer will be added to the ticket.'

// shared skeleton behind all 3 defaults below - intro line, then the title,
// an optional middle section (e.g. {TICKET-MESSAGE}), the view-ticket link,
// a closing note, and the signature
const ticketEmailBody = (intro: string, bodyLines: string[], closingLine: string) => {
    return [
        intro,
        '',
        '{TICKET-TITLE}',
        '',
        ...bodyLines,
        'View ticket details:',
        '{TICKET-URL}',
        '',
        closingLine,
        '',
        '--',
        '{COMPANY-NAME}'
    ].join('\n')
}

const NEW_TICKET_TEXT   = ticketEmailBody('We have received your request.', ['{TICKET-MESSAGE}', ''], REPLY_HINT)
const REPLY_TEXT        = ticketEmailBody('{COMPANY-NAME} has replied to your ticket.', ['{TICKET-MESSAGE}', ''], REPLY_HINT)
const CLOSE_TICKET_TEXT = ticketEmailBody('Your ticket has been closed.', [], 'If you have any further questions, simply reply to this email and your message will be added to the ticket.')

const DEFAULTS : {[templateId: string] : JSONObject} = {

    [TicketEmailTemplateId.NEW_TICKET] : {
        name        : 'New ticket',
        subject     : SUBJECT,
        contentTXT  : NEW_TICKET_TEXT
    },

    [TicketEmailTemplateId.REPLY] : {
        name        : 'Ticket reply',
        subject     : SUBJECT,
        contentTXT  : REPLY_TEXT
    },

    [TicketEmailTemplateId.CLOSE_TICKET] : {
        name        : 'Ticket closed',
        subject     : SUBJECT,
        contentTXT  : CLOSE_TICKET_TEXT
    }
}

const buildTemplate = (templateId: string, savedData: JSONObject) => {
    return new EmailTemplate({
        ...DEFAULTS[templateId],
        ...savedData,
        id          : templateId,                   // fixed slot key - never taken from saved data
        contentType : EmailContentType.TEXT         // ticket emails are text only, the edit form has no picker
    })
}

export default class ProjectSettings_ModuleTickets {

    public emailTemplates : {[templateId: string] : EmailTemplate}
    public fromName       : string   // sender display name for all 3 emails - the address is always task-reply@app.goodday.work

    constructor(config: any) {

        this.emailTemplates = _.mapValues(DEFAULTS, (defaults: JSONObject, templateId: string) => {
            return buildTemplate(templateId, _.get(config, ['emailTemplates', templateId], {}))
        })

        this.fromName = _.get(config, 'fromName', DEFAULT_FROM_NAME)
    }

    toJson() {

        // Only templates the user actually changed are persisted, so a project that
        // never opened this section doesn't carry a copy of the defaults around
        const editedTemplates = _.pickBy(this.emailTemplates, (emailTemplate: EmailTemplate, templateId: string) => {
            return !_.isEqual(emailTemplate.toJSON(), buildTemplate(templateId, {}).toJSON())
        })

        const json : JSONObject = {
            emailTemplates : _.mapValues(editedTemplates, (emailTemplate: EmailTemplate) => {
                return emailTemplate.toJSON()
            })
        }

        // same "only if edited" rule as the templates - an untouched project keeps saving nothing
        if (this.fromName !== DEFAULT_FROM_NAME) {
            json.fromName = this.fromName
        }

        return json
    }
}
